import type { ComponentProps } from "react"
import { useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Placeholder from "@/v2-ui/Placeholder"
import Button from "@/v2-backoffice-client/components/button"
import UptimeConfigPlatformDialog from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigPlatformDialog"
import UptimeConfigPlatformRow from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigPlatformRow"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Platform = UptimeConfigQuery["getStatusConfig"]["Platforms"][number]

export type UptimeConfigPlatformsTabProps = ComponentProps<"div"> & {
  platforms: Platform[]
}

// UptimeConfigPlatformsTab renders the platform catalogue used by
// the public status page's top-level cards. Order is driven by
// `Priority` so the operator can drag the most-important platform
// to the top without touching code; the sort is stable on
// (Priority, Name) so two platforms with the same priority render
// alphabetically.
function UptimeConfigPlatformsTab(props: UptimeConfigPlatformsTabProps) {
  const { platforms, className, ...divProps } = props
  const [editing, setEditing] = useState<Platform | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const sorted = useMemo(() => {
    return [...platforms].toSorted(
      (a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name),
    )
  }, [platforms])

  return (
    <div className={cn("uptime-config-tab", className)} {...divProps}>
      <div className="uptime-config-tab-header">
        <div className="uptime-config-tab-header-text">
          <h3 className="uptime-config-tab-title">Platforms</h3>
          <p className="uptime-config-tab-description">
            Top-level groupings on the public status page (one card per
            platform).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconSrc="plus"
          onClick={() => {
            setIsCreateOpen(true)
          }}
        >
          New platform
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Placeholder className="py-8">
          No platforms configured. Add one to start populating the public status
          page.
        </Placeholder>
      ) : (
        <ul className="uptime-config-list">
          {sorted.map((p) => (
            <UptimeConfigPlatformRow
              key={p.Id}
              platform={p}
              onEdit={() => {
                setEditing(p)
              }}
            />
          ))}
        </ul>
      )}

      <UptimeConfigPlatformDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        mode="create"
      />
      <UptimeConfigPlatformDialog
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) setEditing(null)
        }}
        mode="edit"
        platform={editing}
      />
    </div>
  )
}

export default UptimeConfigPlatformsTab
